import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IconButton } from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useSelector } from 'react-redux';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { getFirstName } from 'src/modules/user/selectors/userSelectors';
import { toastNotificationsService } from 'src/config/rootService';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { Avatar } from 'src/modules/messaging/components/Avatar';
import { usePathwayQuery } from 'src/modules/pathways/documents/pathway.generated';
import { useSubmitPathwayReviewMutation } from 'src/modules/pathways/documents/submitPathwayReview.generated';
import { usePathwaysNavigation } from 'src/modules/pathways/hooks/navigation';
import { PageContentContainer } from 'src/modules/pathways/components/PageLayout';
import {
    Review,
    QuestionFormat,
    QuestionDisplayField,
    MultiSelect,
    Slider,
    NumbersRow,
    Textarea,
    ReviewConfig,
    ReviewState,
    ReviewUiState,
    getValueFromReviewState,
} from 'src/shared/components/Review/Review';
import { ReviewSubmissionSuccess } from 'src/shared/components/Review/ReviewSubmissionSuccess';

// -- review config

export enum PathwayReview {
    RATING = 'rating',
    WHAT_YOU_LIKED_ABOUT_PATHWAY = 'what_you_liked_about_pathway',
    WHAT_COULD_HAVE_BEEN_BETTER = 'what_could_have_been_better',
    CONFIDENCE_IN_INCORPORATING_SKILLS = 'confidence_in_incorporating_skills',
    NPS_LIKELY_TO_RECOMMEND = 'nps_likely_to_recommend',
    FEEDBACK_TEXT = 'feedback_text',
}

export type PathwayReviewState = ReviewState<PathwayReview>;

const whatCouldHaveBeenBetter: MultiSelect<PathwayReview> = {
    key: PathwayReview.WHAT_COULD_HAVE_BEEN_BETTER,
    format: QuestionFormat.MULTI_SELECT,
    title: t => t('pathways:review.question.improve.title'),
    subtitle: t => t('pathways:review.question.improve.subtitle'),
    options: [
        {
            value: 'my_provider',
            label: t => t('pathways:review.question.liked_or_improve.options.my_provider'),
        },
        {
            value: 'number_of_sessions',
            label: t => t('pathways:review.question.liked_or_improve.options.number_of_sessions'),
        },
        {
            value: 'session_length',
            label: t => t('pathways:review.question.liked_or_improve.options.session_length'),
        },
        {
            value: 'session_topics',
            label: t => t('pathways:review.question.liked_or_improve.options.session_topics'),
        },
        {
            value: 'curated_resources',
            label: t => t('pathways:review.question.liked_or_improve.options.curated_resources'),
        },
        {
            value: 'assessments',
            label: t => t('pathways:review.question.liked_or_improve.options.assessments'),
        },
    ],
};

const whatYouLiked: MultiSelect<PathwayReview> = {
    key: PathwayReview.WHAT_YOU_LIKED_ABOUT_PATHWAY,
    format: QuestionFormat.MULTI_SELECT,
    title: t => t('pathways:review.question.liked.title'),
    subtitle: t => t('pathways:review.question.liked.subtitle'),
    options: [
        {
            value: 'my_provider',
            label: t => t('pathways:review.question.liked_or_improve.options.my_provider'),
        },
        {
            value: 'number_of_sessions',
            label: t => t('pathways:review.question.liked_or_improve.options.number_of_sessions'),
        },
        {
            value: 'session_length',
            label: t => t('pathways:review.question.liked_or_improve.options.session_length'),
        },
        {
            value: 'session_topics',
            label: t => t('pathways:review.question.liked_or_improve.options.session_topics'),
        },
        {
            value: 'curated_resources',
            label: t => t('pathways:review.question.liked_or_improve.options.curated_resources'),
        },
        {
            value: 'assessments',
            label: t => t('pathways:review.question.liked_or_improve.options.assessments'),
        },
    ],
};

const confidentInIncorporatingSkills: Slider<PathwayReview> = {
    key: PathwayReview.CONFIDENCE_IN_INCORPORATING_SKILLS,
    format: QuestionFormat.SLIDER,
    inputRange: [1, 5],
    title: t => t('pathways:review.question.incorporating_skills.title'),
    subtitle: t => t('pathways:review.question.incorporating_skills.subtitle'),
    minLabel: t => t('pathways:review.question.incorporating_skills.min_label'),
    maxLabel: t => t('pathways:review.question.incorporating_skills.max_label'),
};

const badReviewQuestionTree = [whatCouldHaveBeenBetter, whatYouLiked];
const goodReviewQuestionTree = [whatYouLiked, whatCouldHaveBeenBetter];

const feedback: Textarea<PathwayReview> = {
    key: PathwayReview.FEEDBACK_TEXT,
    format: QuestionFormat.TEXTAREA,
    title: t => t('pathways:review.question.feedback.title'),
    subtitle: t => t('pathways:review.question.feedback.subtitle'),
    hint: t => t('pathways:review.question.feedback.hint'),
    displayOrder: [
        QuestionDisplayField.HEADER,
        QuestionDisplayField.TITLE,
        QuestionDisplayField.SUBTITLE,
    ],
};

const npsLikelyToRecommend: NumbersRow<PathwayReview> = {
    key: PathwayReview.NPS_LIKELY_TO_RECOMMEND,
    format: QuestionFormat.NUMBERS_ROW,
    inputRange: [0, 10],
    title: t => t('pathways:review.question.nps_recommend.title'),
    minLabel: t => t('pathways:review.question.nps_recommend.min_label'),
    maxLabel: t => t('pathways:review.question.nps_recommend.max_label'),
    feedback: true,
    feedbackHint: t => t('pathways:review.question.nps_recommend.feedback_hint'),
};

export const review: ReviewConfig<PathwayReview> = {
    rating: {
        key: PathwayReview.RATING,
        title: t => t('pathways:review.rating.title'),
        subtitle: t => t('pathways:review.rating.subtitle'),
        questionTree: [
            // 3 or less
            [3, badReviewQuestionTree],
            // Infinity or less until the previously defined rule (aka 3)
            [Infinity, goodReviewQuestionTree],
        ],
    },
    questions: [confidentInIncorporatingSkills, feedback, npsLikelyToRecommend],
};

// -- screen

function useReviewHistoryState() {
    const [[state, action], setState] = useState<
        [(PathwayReviewState | undefined)[], 'push' | 'pop']
    >([[undefined], 'push']);

    const popState = useCallback(() => {
        const newState = state.slice(0, state.length - 1);
        setState([newState, 'pop']);
    }, [state, setState]);

    const pushState = useCallback(
        (nextState: PathwayReviewState) => {
            setState([[...state, nextState], 'push']);
        },
        [state, setState],
    );

    return useMemo(
        () => [state, action, { pushState, popState }] as const,
        [state, action, pushState, popState],
    );
}

export const PathwayReviewPage = () => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const { navigate } = usePathwaysNavigation();
    const { uuid } = useParams<{ uuid: string }>();
    const memberFirstName = useSelector(getFirstName);
    const { data, loading } = usePathwayQuery({
        variables: {
            uuid,
        },
    });
    const [submitPathwayReview, { data: submissionData }] = useSubmitPathwayReviewMutation({});
    const [state, action, { pushState, popState }] = useReviewHistoryState();

    const onSuccessRedirect = useCallback(() => {
        navigate.pathway({ uuid });
    }, [navigate, uuid]);

    if (!data?.pathway?.memberPathway || loading) {
        return <CenteredSpinner />;
    }

    const { memberPathway } = data.pathway;

    if (
        submissionData?.submitPathwayReview?.__typename === 'SubmitPathwayReviewResponse' &&
        submissionData?.submitPathwayReview?.ok
    ) {
        return (
            <ReviewSubmissionSuccess
                title={t('pathways:review.success.title', {
                    name: memberFirstName,
                })}
                subtitle={t('pathways:review.success.subtitle')}
                onRedirect={onSuccessRedirect}
            />
        );
    }

    const activeReviewState = state[state.length - 1];

    return (
        <>
            {state.length > 1 && (
                <IconButton
                    title={t('common.back')}
                    onClick={popState}
                    icon="arrow-left"
                    size="medium"
                    variant="secondary"
                    css={{
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        zIndex: 1,
                    }}
                />
            )}
            <IconButton
                title={t('common.close')}
                onClick={() => navigate.pathway({ uuid })}
                icon="x"
                size="medium"
                variant="secondary"
                css={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    zIndex: 1,
                }}
            />
            <TransitionWrapper action={action}>
                <SwitchTransition>
                    <CSSTransition key={state.length} timeout={300} classNames="review">
                        <PageContentContainer
                            width={485}
                            css={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                paddingTop: 50,
                                paddingBottom: 50,
                            }}
                        >
                            <Review
                                config={review}
                                state={activeReviewState}
                                onNext={pushState}
                                onSubmit={async state => {
                                    try {
                                        const { data } = await submitPathwayReview({
                                            variables: {
                                                input: {
                                                    pathwayUuid: uuid,
                                                    rating: state.rating,
                                                    whatYouLikedAboutPathway:
                                                        getValueFromReviewState(
                                                            whatYouLiked,
                                                            state,
                                                        ),
                                                    whatCouldHaveBeenBetter:
                                                        getValueFromReviewState(
                                                            whatCouldHaveBeenBetter,
                                                            state,
                                                        ),
                                                    confidenceInIncorporatingSkills:
                                                        getValueFromReviewState(
                                                            confidentInIncorporatingSkills,
                                                            state,
                                                        ),
                                                    feedbackText: getValueFromReviewState(
                                                        feedback,
                                                        state,
                                                    ),
                                                    likelyToRecommend: getValueFromReviewState(
                                                        npsLikelyToRecommend,
                                                        state,
                                                    )?.value,
                                                    likelyToRecommendFeedbackText:
                                                        getValueFromReviewState(
                                                            npsLikelyToRecommend,
                                                            state,
                                                        )?.feedback,
                                                },
                                            },
                                        });

                                        if (
                                            !(
                                                data?.submitPathwayReview?.__typename ===
                                                    'SubmitPathwayReviewResponse' &&
                                                data?.submitPathwayReview?.ok
                                            )
                                        ) {
                                            throw new Error('Missing pathway');
                                        }
                                    } catch (e) {
                                        toastNotificationsService.renderErrorToast(
                                            t('pathways:review.submission_error'),
                                        );
                                        // Throw for the Review component to know the submission was a
                                        // failure.
                                        throw e;
                                    }
                                }}
                                onRender={(_, question) =>
                                    trackEvent(
                                        EventLexicon.singlePageFlowPathwayRatingQuestion({
                                            pathwayUuid: uuid,
                                            providerType:
                                                memberPathway.pathwayProvider.provider
                                                    .providerType ?? '',
                                            providerUuid:
                                                memberPathway.pathwayProvider.provider.uuid,
                                            question: question ? question.key : 'rating',
                                        }),
                                    )
                                }
                                renderHeader={(uiState, question) => {
                                    if (!data.pathway?.memberPathway?.pathwayProvider?.provider) {
                                        return null;
                                    }

                                    if (uiState === ReviewUiState.RATING) {
                                        const { avatar, firstName, lastName } =
                                            data.pathway.memberPathway.pathwayProvider.provider;

                                        return (
                                            <Avatar
                                                user={{
                                                    avatar,
                                                    firstName,
                                                    lastName,
                                                }}
                                                diameter={180}
                                                css={{ marginBottom: 30 }}
                                            />
                                        );
                                    }

                                    if (
                                        [
                                            ReviewUiState.QUESTION_WITH_RATING,
                                            ReviewUiState.QUESTION,
                                        ].includes(uiState)
                                    ) {
                                        if (
                                            question &&
                                            [
                                                PathwayReview.FEEDBACK_TEXT,
                                                PathwayReview.NPS_LIKELY_TO_RECOMMEND,
                                            ].includes(question.key)
                                        ) {
                                            return null;
                                        }
                                        const { avatar, firstName, lastName } =
                                            data.pathway.memberPathway.pathwayProvider.provider;

                                        return (
                                            <Avatar
                                                user={{
                                                    avatar,
                                                    firstName,
                                                    lastName,
                                                }}
                                                diameter={100}
                                                css={{ marginBottom: 20 }}
                                            />
                                        );
                                    }

                                    return null;
                                }}
                            />
                        </PageContentContainer>
                    </CSSTransition>
                </SwitchTransition>
            </TransitionWrapper>
        </>
    );
};

const TransitionWrapper = styled.div<{ action: 'push' | 'pop' }>(({ action }) => ({
    flex: 1,
    display: 'flex',
    justifyContent: 'center',

    '.review-enter': {
        opacity: 0,
        transform: `translate(${action === 'push' ? '50%' : '-50%'}, 0)`,
    },
    '.review-enter-active': {
        opacity: 1,
        transition: 'transform 250ms ease-in, opacity 350ms ease-in',
        transform: 'translate(0,0)',
    },
    '.review-exit': {
        transform: 'translate(0,0)',
    },
    '.review-exit-active': {
        opacity: 0,
        transform: `translate(${action === 'push' ? '-25%' : '25%'}, 0)`,
        transition: 'opacity 200ms ease-out, transform 250ms',
    },
}));
